import { Component, effect, input, model, output, untracked, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, FormGroupDirective, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import moment from 'moment';
import { OperateurSitesService } from 'src/app/data/declaration/services/operateur-sites/operateur-sites.service';
import { NavigationContext, SessionContext } from 'src/app/core/services/config/app.settings';
import { Site } from 'src/app/data/declaration/models/site.model';
import { Produit } from 'src/app/data/habilitation/models/produit.model';
import { UtilisateurTypeCode } from 'src/app/data/intervenant/models/enums/type-utilisateur.enum';
import { Cepage } from 'src/app/data/declaration/models/cepage.model';
import { MouvementDestination } from 'src/app/data/declaration/models/mouvement-destination.model';
import { SaisieRevendication } from 'src/app/data/declaration/models/saisie-revendication.model';
import { ReferentielService } from 'src/app/data/declaration/services/referentiel/referentiel.service';
import { LotSaisiesRevendications } from 'src/app/data/declaration/models/lot-saisies-revendications.model';
import { ProduitsService } from 'src/app/data/habilitation/services/produits/produits.service';
import { CahiersService } from 'src/app/data/habilitation/services/cahiers/cahiers.service';
import { Cahier } from 'src/app/data/habilitation/models/cahier.model';
import { ActiviteCode } from 'src/app/data/habilitation/models/enums/activites-code.enum';
import { RefStatutCode } from 'src/app/data/habilitation/models/enums/ref-statut-code.enum';
import { TypeRevendication } from '../../../../../../../data/declaration/models/enums/type-revendication.enum';
import { EnvironnementLabel } from '../../../../../../../data/declaration/models/environnement-label.model';
import { RevendicationType } from '../../../../../../../data/declaration/revendication-type.model';
import { Referentiel } from '../../../../../../../data/declaration/models/referentiel.model';
import { LoaderService } from '../../../../../../../core/services/loader/loader.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { LABELS_ENVIRONNEMENTAUX } from '../../../../../../../data/declaration/models/constants/labels-environnementaux.constant';
import { Dialog } from 'primeng/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { FloatLabel } from 'primeng/floatlabel';
import { InputText } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { Button } from 'primeng/button';
import { NgClass } from '@angular/common';
import { Select } from 'primeng/select';
import { MultiSelect } from 'primeng/multiselect';
import { InputNumber } from 'primeng/inputnumber';
import { Textarea } from 'primeng/textarea';

@Component({
    selector: 'app-saisir-revendication',
    templateUrl: './saisir-revendication.component.html',
    styleUrls: ['./saisir-revendication.component.scss'],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        Dialog,
        TranslateModule,
        FloatLabel,
        InputText,
        DatePickerModule,
        Button,
        NgClass,
        Select,
        MultiSelect,
        InputNumber,
        Textarea
    ],
    standalone: true
})
export class SaisirRevendicationComponent {
    @ViewChild(FormGroupDirective) formDirective: FormGroupDirective;

    // Signals
    readonly campagne = input<string>();
    readonly lotRevendicationsInput = input<LotSaisiesRevendications>(undefined, { alias: 'lotRevendications' });
    readonly indexModificationInput = input<number>(undefined, { alias: 'indexModification' });
    visible = model<boolean>(false);
    readonly terminer = output<LotSaisiesRevendications>();

    // Properties
    lotRevendications: LotSaisiesRevendications;
    indexModification: number;
    produits: Produit[];
    produitsAll: Produit[];
    cepages: Cepage[];
    destinations: MouvementDestination[];
    idOperateur: number;
    idOrganisme: number;
    sites: Site[];
    public formGroup: UntypedFormGroup;
    cahiers: Cahier[];
    environnementLabels: EnvironnementLabel[];
    revendicationTypes: RevendicationType[];
    readonly TypeRevendication = TypeRevendication;
    readonly revendicationTypeOptions = [
        { label: 'Partielle', value: TypeRevendication.PARTIELLE },
        { label: 'Totale', value: TypeRevendication.TOTALE }
    ];
    referentiels: Referentiel;

    // Getters formulaire
    get isAucunEnvLabelSelected(): boolean {
        const selected = this.form_labelsEnv.value || ([] as EnvironnementLabel[]);
        return Array.isArray(selected) && selected.some(l => this.isAucunLabel(l));
    }
    get isAutreEnvLabelSelected(): boolean {
        const selected = this.form_labelsEnv.value || ([] as EnvironnementLabel[]);
        return Array.isArray(selected) && selected.some(l => this.isAutreLabel(l));
    }
    get form_produits() {
        return this.formGroup.get('produits');
    }
    get form_cepages() {
        return this.formGroup.get('cepages');
    }
    get form_date() {
        return this.formGroup.get('date');
    }
    get form_logement() {
        return this.formGroup.get('logement');
    }
    get form_lot() {
        return this.formGroup.get('lot');
    }
    get form_degre() {
        return this.formGroup.get('degre');
    }
    get form_observation() {
        return this.formGroup.get('observation');
    }
    get form_volume() {
        return this.formGroup.get('volume');
    }
    get form_entreposage() {
        return this.formGroup.get('entreposage');
    }
    get form_destinations() {
        return this.formGroup.get('destinations');
    }
    get form_cahier() {
        return this.formGroup.get('cahier');
    }
    get form_type() {
        return this.formGroup.get('type');
    }
    get form_labelsEnv() {
        return this.formGroup.get('environnementLabels');
    }
    get form_autresLabelsEnv() {
        return this.formGroup.get('autresEnvironnementLabels');
    }

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly operateurSitesService: OperateurSitesService,
        private readonly referentielService: ReferentielService,
        private readonly loaderService: LoaderService,
        private readonly produitsService: ProduitsService,
        private readonly cahiersService: CahiersService
    ) {
        // Création du formulaire une seule fois
        this.formGroup = this.fb.group({
            cahier: [undefined, Validators.required],
            produits: [undefined, Validators.required],
            cepages: [undefined],
            date: [undefined, Validators.required],
            logement: [undefined, Validators.required],
            lot: [undefined, Validators.required],
            degre: [undefined, this.positifOrNullValidator],
            observation: [undefined],
            volume: [undefined, [Validators.pattern(/(^\d+$)|(^\d+\.\d{1,2}$)|(^\d+,\d{1,2}$)/), this.positifStrictValidator, Validators.required]],
            entreposage: [undefined, Validators.required],
            destinations: [undefined, Validators.required],
            type: [TypeRevendication.PARTIELLE],
            environnementLabels: [undefined, Validators.required],
            autresEnvironnementLabels: [undefined]
        });
        this.setupEnvironnementLabelsExclusivity();

        // Initialisation à chaque ouverture du dialog
        // untracked() empêche les lectures secondaires de déclencher l'effet :
        // seul le passage visible() false → true provoque la réinitialisation.
        effect(() => {
            if (this.visible()) {
                untracked(() => {
                    this.lotRevendications = this.lotRevendicationsInput();
                    this.indexModification = this.indexModificationInput();

                    this.idOperateur =
                        this.lotRevendications?.idOperateur ??
                        (SessionContext.get('utilisateurType') === UtilisateurTypeCode.OPERATEUR
                            ? SessionContext.get('idIntervenant')
                            : NavigationContext.get('idOperateur'));
                    this.idOrganisme = SessionContext.get('idOrganisme');

                    // Réinitialise le formulaire (conserve uniquement le type par défaut)
                    this.formGroup.reset({ type: TypeRevendication.PARTIELLE });
                    this.formGroup.enable();

                    this.loadData();
                });
            }
        });
    }

    // ── Chargement des données ─────────────────────────────────────────────

    /**
     * Charge les données nécessaires au formulaire et le pré-remplit dans le cas
     * d'un ajout ou d'une modification depuis un autre composant
     */
    loadData() {
        // Charger les stocks, les contenants et les adresses
        forkJoin([
            this.referentielService.getReferentiel(),
            this.operateurSitesService.getSitesOperateur(this.idOperateur),
            this.cahiersService.getCahiersByHabilitation(this.idOperateur, this.idOrganisme, RefStatutCode.Habilite, ActiviteCode.VIN_PROD_VIGNIFICATION)
        ])
            .pipe(this.loaderService.operator())
            .subscribe(([ref, sites, cahiers]) => {
                this.cepages = ref.cepages.sort((a, b) => a.libelle.localeCompare(b.libelle));
                this.destinations = ref.mouvementsDestinations;
                this.sites = sites;
                this.cahiers = cahiers;
                this.environnementLabels = this.sortEnvironnementLabels(ref.environnementLabels);
                this.revendicationTypes = ref.revendicationTypes;
                this.referentiels = ref;

                this.produitsService.getProduitsByCdcs(this.idOrganisme, [this.cahiers[0].id]).subscribe(produits => {
                    this.produitsAll = produits;
                });
                if (this.lotRevendications?.revendications?.length > 0) {
                    this.preRemplirForm();
                }
            });
    }

    // ── Règles labels environnementaux ─────────────────────────────────────

    /**
     * Application des règles sur les champs "labels environnementaux" et "autres labels environnementaux"
     */
    private setupEnvironnementLabelsExclusivity() {
        this.form_labelsEnv.valueChanges.pipe(distinctUntilChanged()).subscribe((labels: EnvironnementLabel[]) => {
            const safeLabels = Array.isArray(labels) ? labels : [];
            if (safeLabels.length === 0) {
                this.updateAutresEnvironnementLabelsValidator([]);
                return;
            }
            const aucun = safeLabels.find(l => this.isAucunLabel(l));
            if (aucun && safeLabels.length > 1) {
                this.form_labelsEnv.setValue([aucun], { emitEvent: false });
                this.updateAutresEnvironnementLabelsValidator([aucun]);
                return;
            }
            this.updateAutresEnvironnementLabelsValidator(safeLabels);
        });
    }

    /**
     * Met à jour le validateur pour le champ "autres labels environnementaux"
     * @param labels - Liste des labels environnementaux
     */
    private updateAutresEnvironnementLabelsValidator(labels: EnvironnementLabel[]) {
        if (!this.form_autresLabelsEnv) {
            return;
        }
        const safeLabels = Array.isArray(labels) ? labels : [];
        const autreSelected = safeLabels.some(l => this.isAutreLabel(l));
        const aucunSelected = safeLabels.some(l => this.isAucunLabel(l));
        if (autreSelected) {
            this.form_autresLabelsEnv.setValidators([Validators.required]);
        } else if (aucunSelected) {
            this.clearAutresLabelsEnv();
            this.form_autresLabelsEnv.disable({ emitEvent: false });
        } else {
            this.clearAutresLabelsEnv();
            this.form_autresLabelsEnv.enable({ emitEvent: false });
        }
        this.form_autresLabelsEnv.updateValueAndValidity({ emitEvent: false });
    }

    /**
     * Réinitialise le champ "autres labels environnementaux"
     */
    private clearAutresLabelsEnv() {
        this.form_autresLabelsEnv.setValue(undefined, { emitEvent: false });
        this.form_autresLabelsEnv.clearValidators();
    }

    /**
     * Détermine si le label est "Aucun"
     * @param label - Le label à tester
     */
    private isAucunLabel(label: EnvironnementLabel): boolean {
        return (label.libelle || '').trim().toLowerCase() === LABELS_ENVIRONNEMENTAUX.AUCUN;
    }

    /**
     * Détermine si le label est "Autre"
     * @param label - Le label à tester
     */
    private isAutreLabel(label: EnvironnementLabel): boolean {
        return (label.libelle || '').trim().toLowerCase() === LABELS_ENVIRONNEMENTAUX.AUTRE;
    }

    /**
     * Ordonne les labels environnementaux
     * @param labels - Liste des labels
     */
    private sortEnvironnementLabels(labels: EnvironnementLabel[]): EnvironnementLabel[] {
        if (!Array.isArray(labels)) {
            return [];
        }
        return [...labels].sort((a, b) => {
            const w = this.weightLabels(a) - this.weightLabels(b);
            return w !== 0 ? w : (a.libelle || '').localeCompare(b.libelle || '');
        });
    }

    /**
     * Donne le poids d'un label environnement
     * @param label - Le label à évaluer
     */
    private weightLabels(label: EnvironnementLabel): number {
        if (this.isAucunLabel(label)) {
            return 0;
        }
        if (this.isAutreLabel(label)) {
            return 2;
        }
        return 1;
    }

    // ── Validateurs ────────────────────────────────────────────────────────

    positifStrictValidator(control: UntypedFormControl) {
        return control.value <= 0 ? { invalid: true } : null;
    }

    /**
     * Vérifie qu'un contrôle de formulaire est positif ou null
     * @param control Le contrôle à vérifier
     */
    positifOrNullValidator(control: UntypedFormControl) {
        return control.value && control.value <= 0 ? { invalid: true } : null;
    }

    // ── Actions ────────────────────────────────────────────────────────────

    onClose() {
        this.visible.set(false);
    }

    onAjouterLot() {
        this.onSubmit(this.formDirective);
    }

    onSubmit(formDirective: FormGroupDirective = null) {
        if (this.formGroup.valid) {
            // Instancier la revendication saisie
            const saisie = this.instanciateSaisie();

            // Instancier le lot si c'est la première saisie
            if (!this.lotRevendications) {
                this.lotRevendications = new LotSaisiesRevendications(
                    this.form_cahier.value.id,
                    this.form_cahier.value.libelle,
                    saisie.date,
                    saisie.entreposage,
                    this.campagne(),
                    this.idOperateur,
                    this.referentiels
                );
            }

            if (this.indexModification > -1) {
                // Mettre à jour la revendication dans le lot
                const idMouvement = this.lotRevendications.revendications[this.indexModification].id;
                this.lotRevendications.revendications[this.indexModification] = saisie;
                this.lotRevendications.revendications[this.indexModification].id = idMouvement;
            } else {
                // Ajouter la revendication saisie au lot
                this.lotRevendications.revendications.push(saisie);
            }

            // Réinitialiser le formulaire pour la saisie suivante
            if (formDirective) {
                this.resetForm(formDirective, saisie);
            }
        }
    }

    onTerminer() {
        // Récupérer la saisie du formulaire courant
        this.onSubmit();
        this.terminer.emit(this.lotRevendications);
        this.visible.set(false);
    }

    // ── Formulaire ─────────────────────────────────────────────────────────

    /**
     * Réinitialise le formulaire pour une nouvelle saisie avec entête pré-remplie et
     * non modifiable
     * @param form Le formulaire à réinitialiser
     * @param saisie La saisie contenant l'entête à afficher
     * @see SaisieRevendication
     */
    resetForm(form: FormGroupDirective, saisie: SaisieRevendication) {
        const igpSelect: Cahier = this.form_cahier.value;
        // Réinitialiser le formulaire et ses validators
        form.resetForm({
            cahier: igpSelect,
            produits: undefined,
            date: saisie.date.toDate(),
            entreposage: saisie.entreposage,
            cepages: undefined,
            logement: undefined,
            lot: undefined,
            degre: undefined,
            volume: undefined,
            destinations: undefined,
            observation: undefined
        });

        // Désactiver les champs cahier, date et entreposage après la première saisie
        this.form_date.disable();
        this.form_entreposage.disable();
        this.form_cahier.disable();
        this.form_type.disable();
        // Réinitialiser l'index de modification
        this.indexModification = undefined;
    }

    /**
     * Retourne un objet de la classe SaisieRevendication en fonction de la saisie
     * effectuée par l'utilisateur
     * @see SaisieRevendication
     */
    instanciateSaisie(): SaisieRevendication {
        const selectedType = this.form_type?.value
            ? this.revendicationTypes?.find(r => r.libelle.toLowerCase() === String(this.form_type.value).toLowerCase())
            : undefined;

        return Object.assign(new SaisieRevendication(), {
            codeProduit: this.form_produits?.value?.code,
            libelleProduit: this.form_produits?.value?.libelle,
            cepages: (this.form_cepages?.value ?? []).map((c: any) => c.id),
            destinations: (this.form_destinations?.value ?? []).map((d: any) => d.id),
            degre: this.form_degre?.value,
            date: this.form_date?.value ? moment(this.form_date.value) : undefined,
            logement: this.form_logement?.value,
            numeroLot: this.form_lot?.value,
            volume: this.form_volume?.value,
            entreposage: this.form_entreposage?.value,
            observation: this.form_observation?.value,
            type: selectedType?.libelle,
            environnementLabels: (this.form_labelsEnv?.value
                ? this.form_labelsEnv.value.filter((l: EnvironnementLabel) => l.libelle.trim().toLowerCase() !== LABELS_ENVIRONNEMENTAUX.AUTRE)
                : []
            ).map((env: EnvironnementLabel) => env.id),
            autresEnvironnementLabels: this.form_autresLabelsEnv?.value
        });
    }

    /**
     * Pré-remplit le formulaire avec les données de la revendication
     * à modifier ou avec les données d'entête dans le cas d'un ajout
     * a posteriori
     */
    preRemplirForm() {
        // Vérifier si l'on est en modification ou en ajout d'une revendication
        const cahier: Cahier = this.cahiers.find(c => c.id === this.lotRevendications.idCahier);
        this.loadProductsByCdc([cahier.id]);

        if (this.indexModification > -1) {
            const saisie = this.lotRevendications.revendications[this.indexModification];

            this.produitsService.getProduitsByCdcs(this.idOrganisme, [cahier.id]).subscribe(produitSearch => {
                const environnementLabelsSelectionnes = (saisie.environnementLabels || [])
                    .map(env => this.environnementLabels.find(e => e.id === env))
                    .filter(env => !!env);

                if (saisie.autresEnvironnementLabels?.trim()) {
                    const autreLabel = this.environnementLabels.find(e => e.libelle === 'Autre');
                    if (autreLabel && !environnementLabelsSelectionnes.some(e => e.id === autreLabel.id)) {
                        environnementLabelsSelectionnes.push(autreLabel);
                    }
                }

                this.formGroup.patchValue({
                    cahier,
                    produits: produitSearch.find(prd => prd.code === saisie.codeProduit),
                    date: saisie.date.toDate(),
                    entreposage: this.preRemplirEntreposage(saisie.entreposage),
                    cepages: saisie.cepages.map(cepage => this.cepages.find(c => c.id === cepage)) || [],
                    logement: saisie.logement,
                    lot: saisie.numeroLot,
                    degre: saisie.degre,
                    volume: saisie.volume,
                    destinations: saisie.destinations.map(dest => this.destinations.find(d => d.id === dest)) || [],
                    observation: saisie.observation,
                    type: saisie.type?.toLowerCase() === TypeRevendication.PARTIELLE || !saisie.type ? TypeRevendication.PARTIELLE : TypeRevendication.TOTALE,
                    environnementLabels: environnementLabelsSelectionnes,
                    autresEnvironnementLabels: saisie.autresEnvironnementLabels
                });

                // Désactivation APRÈS patchValue pour que p-datePicker affiche la valeur
                this.form_date.disable();
                this.form_entreposage.disable();
                this.form_cahier.disable();
                this.form_type.disable();
            });
        } else {
            const saisie = this.lotRevendications.revendications[0];
            this.formGroup.patchValue({
                cahier,
                date: saisie.date.toDate(),
                entreposage: this.sites.find(site => site.id === saisie.entreposage.id)
            });

            // Désactivation APRÈS patchValue
            this.form_date.disable();
            this.form_entreposage.disable();
            this.form_cahier.disable();
            this.form_type.disable();
        }
    }

    preRemplirEntreposage(entreposage: Site): Site {
        const isPresent = this.sites.find(site => site.id === entreposage.id);
        if (!isPresent) {
            this.sites.push(entreposage);
            return entreposage;
        }
        return isPresent;
    }

    loadProducts() {
        // Récupérer l'ID du CDCs sélectionné
        const idsCdcs: number[] = [this.form_cahier.value.id];
        this.loadProductsByCdc(idsCdcs);
    }

    /**
     * Charge les produits en fonction du cahier des charges selectionné
     */
    loadProductsByCdc(idsCdcs: number[]) {
        this.produits = [];
        this.form_produits.disable();

        // Réinitialiser la sélection des produits
        this.form_produits.reset();

        // Récupérer les produits liés au CDC sélectionné
        this.produitsService.getProduitsByCdcs(this.idOrganisme, idsCdcs).subscribe(produits => {
            this.produits = produits;
            this.form_produits.enable();
        });
    }
}
